/**
 * One day, every employee the scope reaches — and the subject picker for the month.
 *
 * `GET /attendance?date_from=D&date_to=D&department_id=<optional>`, one day wide, so
 * the page boundary is a boundary in the **employee** list and nothing else. That is
 * the counterpart of `MonthCalendar`'s choice; see its header for why the two reads
 * on this screen are shaped this way.
 *
 * It doubles as the employee filter's source of options, and that is not a
 * convenience: there is **no `GET /employees`** anywhere in `openapi.yaml`. The only
 * published way to learn who is in scope is to ask an endpoint that answers per
 * employee, and this is one — every `(employee, date)` cell carries `employee_name`.
 *
 * ── The status filter is not a scope control, and the copy says so ──────────
 *
 * `GET /attendance` takes `employee_id` and `department_id` and **no status
 * parameter**, so filtering by status can only happen over the rows already
 * fetched. That is a real difference and it is stated on screen rather than hidden:
 * "nobody was late" over a truncated page is a false sentence, so when the filter is
 * on, the note names how many rows it ran over and how many cells the window holds.
 *
 * `web/src/nav/menu.ts`'s rule applies to all three filters without amendment: the
 * server decides scope, and hiding a row here changes nothing about that.
 */

import { useEffect, useMemo, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatDay, formatInstant } from '../../i18n/datetime';
import { attendanceGridPath } from '../../api/paths';
import type {
  AttendanceBadgeStatus,
  AttendanceGridDay,
  AttendanceGridResponse,
} from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import {
  readDuration,
  readMetric,
  renderReading,
  STATUS_PRESENTATION,
} from '../../attendance/status';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { EmptyState } from '../../states/EmptyState';
import { PartialData } from '../../states/PartialData';
import { Skeleton } from '../../states/Skeleton';
import { StatusBadge } from './StatusBadge';

/** The endpoint's own ceiling (`pagination` in `src/modules/attendance/routes.ts`). */
const ROSTER_LIMIT = 200;

export interface RosterListProps {
  readonly date: string;
  readonly timezone: string;
  readonly departmentId: string | null;
  /** `''` means every status. */
  readonly statusFilter: AttendanceBadgeStatus | '';
  readonly selectedEmployeeId: string | null;
  readonly onSelectEmployee: (day: AttendanceGridDay) => void;
  readonly onOpenDay: (employeeId: string, date: string) => void;
  /** Handed back up so the screen's employee `<select>` only offers subjects that exist. */
  readonly onRoster: (rows: readonly AttendanceGridDay[]) => void;
  readonly reloadToken?: number;
}

export function RosterList({
  date,
  timezone,
  departmentId,
  statusFilter,
  selectedEmployeeId,
  onSelectEmployee,
  onOpenDay,
  onRoster,
  reloadToken = 0,
}: RosterListProps): ReactNode {
  const { t, locale } = useI18n();
  const dateLabel = formatDay(date, locale, { day: 'numeric', month: 'long', year: 'numeric' });
  const path = attendanceGridPath({
    from: date,
    to: date,
    limit: ROSTER_LIMIT,
    departmentId: departmentId ?? undefined,
  });
  const { status, data, error, reload } = useApiQuery<AttendanceGridResponse>(path, reloadToken);

  const rows = useMemo(() => data?.data ?? [], [data]);
  const shown = useMemo(
    () =>
      statusFilter === '' ? rows : rows.filter((row) => row.attendance_status === statusFilter),
    [rows, statusFilter],
  );

  // The request belongs to this component, so the roster is reported upward rather
  // than lifted: a second fetch in the screen for the same list would be a second
  // copy that can disagree with this one.
  useEffect(() => {
    onRoster(rows);
  }, [rows, onRoster]);

  const durations = { hour: t('common.hourShort'), minute: t('common.minuteShort') };
  const total = data?.total ?? rows.length;

  return (
    <section className="wp-section" aria-labelledby="k3-roster-heading">
      <h2 className="wp-section__title" id="k3-roster-heading">
        {t('k3.roster.heading', { date: dateLabel })}
      </h2>

      {status === 'loading' ? (
        <Skeleton
          rows={4}
          variant="block"
          label={t('k3.roster.loading')}
          testId="k3-roster-skeleton"
        />
      ) : null}

      {status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={reload}
          subject={t('k3.roster.subject')}
          testId="k3-roster-error"
        />
      ) : null}

      {status === 'ok' && rows.length === 0 ? (
        // A `200` whose grid is empty: the scope reaches nobody. Not a day with no
        // data — that would be a populated row carrying `data_incomplete`, because
        // the grid returns a cell for every (employee, date) pair in the window.
        <EmptyState
          title={t('k3.roster.empty.title')}
          description={t('k3.roster.empty.body')}
          testId="k3-roster-empty"
        />
      ) : null}

      {status === 'ok' && rows.length > 0 ? (
        <>
          {rows.length < total ? (
            <PartialData
              excludes={t('k3.roster.truncated', { shown: rows.length, total })}
              testId="k3-roster-truncated"
            />
          ) : null}

          {statusFilter === '' ? null : (
            <p className="wp-small wp-muted" data-testid="k3-roster-status-filter">
              {t('k3.roster.statusFilter', {
                status: t(STATUS_PRESENTATION[statusFilter].labelKey),
                shown: rows.length,
                total,
              })}
            </p>
          )}

          {shown.length === 0 ? (
            <p data-testid="k3-roster-none-match">{t('k3.roster.noneMatchStatus')}</p>
          ) : (
            <div className="wp-table-scroll">
              <table className="wp-table" data-testid="k3-roster">
                <thead>
                  <tr>
                    <th scope="col">{t('k3.roster.col.employee')}</th>
                    <th scope="col">{t('k3.roster.col.status')}</th>
                    <th scope="col">{t('k3.roster.col.first')}</th>
                    <th scope="col">{t('k3.roster.col.last')}</th>
                    <th scope="col">{t('k3.roster.col.online')}</th>
                    <th scope="col">{t('k3.roster.col.missing')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => (
                    <tr
                      key={row.employee_id}
                      data-testid={`k3-roster-row-${row.employee_id}`}
                      {...(row.employee_id === selectedEmployeeId
                        ? { 'aria-current': 'true' }
                        : {})}
                    >
                      <th scope="row">
                        <button
                          type="button"
                          className="wp-linklike"
                          onClick={() => {
                            onSelectEmployee(row);
                          }}
                          aria-label={t('k3.roster.select', { name: row.employee_name })}
                          data-testid={`k3-select-${row.employee_id}`}
                        >
                          {row.employee_name}
                        </button>
                      </th>
                      <td>
                        <button
                          type="button"
                          className="wp-linklike"
                          onClick={() => {
                            onOpenDay(row.employee_id, row.date);
                          }}
                          aria-label={t('k3.roster.open', {
                            name: row.employee_name,
                            date: dateLabel,
                          })}
                          data-testid={`k3-roster-open-${row.employee_id}`}
                        >
                          <StatusBadge
                            status={row.attendance_status}
                            testId={`k3-roster-badge-${row.employee_id}`}
                          />
                        </button>
                      </td>
                      <td>
                        {row.first_activity_at === null
                          ? t('attendance.noFirst')
                          : formatInstant(row.first_activity_at, locale, timezone)}
                      </td>
                      <td>
                        {row.last_activity_at === null
                          ? t('attendance.noLast')
                          : formatInstant(row.last_activity_at, locale, timezone)}
                      </td>
                      {/* Both cells go through `renderReading`, which has no branch that
                          turns an absence into a zero. `online_minutes` is `null` on a
                          day with no session row; `missing_minutes` is `null` whenever
                          the day is not `complete`. */}
                      <td data-testid={`k3-roster-online-${row.employee_id}`}>
                        {renderReading(readDuration(row.online_minutes, row.reason), durations, t)}
                      </td>
                      <td data-testid={`k3-roster-missing-${row.employee_id}`}>
                        {renderReading(
                          readMetric(row.missing_minutes, row.withheld, 'missing_minutes'),
                          durations,
                          t,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="wp-small wp-muted">{t('k3.filter.note')}</p>
        </>
      ) : null}
    </section>
  );
}
